import React, { useState } from 'react';
import { emailService } from './utils/emailService';

const offers = [
  {
    rate: '8.5%*',
    time: 'within 48 hrs',
    docs: ['Pan card', 'Adhar card', 'Property documents', 'Income proof'],
  },
  {
    rate: '8.75%*',
    time: 'within 72 hrs',
    docs: ['Pan card', 'Adhar card', 'Property documents', 'Bank statements'],
  },
  {
    rate: '9.0%*',
    time: 'within 24 hrs',
    docs: ['Pan card', 'Adhar card', 'Property documents'],
  },
];

const testimonials = [
  {
    quote: 'Securing a mortgage loan with Avalue Capital was seamless and transparent. The team guided me through every step and ensured a quick disbursal.',
    name: 'Rohit Sharma',
    title: 'Homeowner',
  },
  {
    quote: 'I was able to refinance my existing home loan at a better rate and reduce my monthly EMI. The documentation support was excellent.',
    name: 'Neha Gupta',
    title: 'Working Professional',
  },
  {
    quote: 'Being self-employed, I was worried about meeting eligibility norms, but the team structured my application properly and helped me get a sanction quickly.',
    name: 'Arun Mehta',
    title: 'Entrepreneur',
  },
];

const SectionDivider = () => (
  <div className="w-full flex justify-center">
    <svg height="8" width="100%" className="animate-pulse">
      <defs>
        <linearGradient id="lineGradMortgage" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#66a9d6" />
          <stop offset="100%" stopColor="#e63946" />
        </linearGradient>
      </defs>
      <rect width="100%" height="8" rx="4" fill="url(#lineGradMortgage)" />
    </svg>
  </div>
);

const MortgageLoanPage = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', pan: '', property: '', amount: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await emailService.sendMortgageLoanEmail(form);

      if (result.success) {
        setSubmitMessage('Application submitted successfully! We will contact you soon.');
        setForm({ name: '', email: '', phone: '', pan: '', property: '', amount: '' });
      } else {
        setSubmitMessage('Failed to submit application. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-100 via-white to-red-100 overflow-x-hidden">
      {/* Hero Section */}
      <section className="w-full relative flex flex-col md:flex-row items-center justify-between px-4 md:px-0 py-12 md:py-20 bg-gradient-to-br from-blue-200 via-white to-red-100 overflow-hidden">
        {/* Decorative SVG */}
        <svg className="absolute top-0 left-0 w-48 h-48 opacity-10 -z-10" viewBox="0 0 100 100"><ellipse cx="50" cy="50" rx="40" ry="20" fill="#66a9d6" /></svg>
        <div className="flex-1 flex flex-col items-start justify-center z-10 max-w-2xl w-full mb-12 md:mb-0 px-2 md:px-16">
          <h1 className="text-3xl md:text-5xl font-extrabold text-blue-900 mb-4 leading-tight">Unlock Your Dream Home with <span className="text-red-500">Avalue Capital</span></h1>
          <p className="text-lg md:text-xl text-gray-700 mb-6">Get the best mortgage loan offers with fast processing, minimal paperwork, and expert guidance every step of the way.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 w-full">
            <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center">
              <span className="text-xl font-bold text-blue-700">8.5%*</span>
              <span className="text-xs text-gray-500">Interest Rate onwards</span>
            </div>
            <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center">
              <span className="text-xl font-bold text-blue-700">48* hrs</span>
              <span className="text-xs text-gray-500">Less than</span>
            </div>
            <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center">
              <span className="text-xl font-bold text-blue-700">100% Digital</span>
              <span className="text-xs text-gray-500">Processes</span>
            </div>
            <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center">
              <span className="text-xl font-bold text-blue-700">98%</span>
              <span className="text-xs text-gray-500">Approval Rate</span>
            </div>
          </div>
        </div>
        {/* Form Card */}
        <div className="flex-1 max-w-md w-full bg-white/90 backdrop-blur rounded-2xl shadow-2xl p-8 md:mr-16 z-10">
          <h2 className="text-2xl font-bold mb-2 text-blue-700">Mortgage Loan Application</h2>
          <p className="text-sm text-gray-500 mb-4">Complete the form with precise details to enhance your chances of approval.</p>
          <form onSubmit={handleSubmit} className="space-y-3">
            <input name="name" required placeholder="Full Name" className="w-full border rounded px-3 py-2" value={form.name} onChange={handleChange} />
            <input name="email" type="email" required placeholder="Email" className="w-full border rounded px-3 py-2" value={form.email} onChange={handleChange} />
            <input name="phone" required placeholder="Phone" className="w-full border rounded px-3 py-2" value={form.phone} onChange={handleChange} />
            <input name="pan" required placeholder="PAN Number" className="w-full border rounded px-3 py-2" value={form.pan} onChange={handleChange} />
            <input name="property" required placeholder="Property Details" className="w-full border rounded px-3 py-2" value={form.property} onChange={handleChange} />
            <input name="amount" required placeholder="Loan Amount Required" className="w-full border rounded px-3 py-2" value={form.amount} onChange={handleChange} />
            <button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-red-500 hover:from-blue-600 hover:to-red-600 text-white font-semibold py-2 rounded shadow-lg transition" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            {submitMessage && (
              <div className={`text-center font-semibold mt-2 ${submitMessage.includes('successfully') ? 'text-green-600' : 'text-red-600'}`}>
                {submitMessage}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">Furnish the required details and click on "Submit" for expedited loan processing.</p>
          </form>
        </div>
      </section>

      <SectionDivider />

      {/* Steps Section */}
      <section className="w-full relative py-12 bg-gradient-to-r from-blue-50 via-white to-blue-100 overflow-hidden">
        <svg className="absolute right-0 top-0 w-32 h-32 opacity-10 animate-spin-slow" viewBox="0 0 100 100"><rect x="20" y="20" width="60" height="60" rx="20" fill="#66a9d6" /></svg>
        <h3 className="text-3xl font-bold text-blue-700 mb-10 text-center relative z-10">How to Get Your Mortgage Loan</h3>
        <div className="relative flex flex-col md:flex-row md:justify-between md:items-start w-full gap-12 md:gap-0 px-0 max-w-5xl mx-auto z-10">
          {/* Step 1 */}
          <div className="relative flex-1 flex flex-col items-center text-center z-10">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-white bg-blue-500 rounded-full w-12 h-12 flex items-center justify-center shadow-lg border-4 border-white mb-2">1</span>
              <span className="text-4xl md:text-5xl mb-2">📝</span>
            </div>
            <h4 className="font-semibold mt-2 mb-1 text-lg text-blue-800">Fill the form</h4>
            <p className="text-gray-600 max-w-xs">Provide the information required in adjacent form to assess your Loan eligibility.</p>
          </div>
          {/* Connector 1->2 */}
          <div className="hidden md:block absolute left-[18%] top-10 w-[22%] h-24 z-0 pointer-events-none">
            <svg width="100%" height="100%" viewBox="0 0 180 80" fill="none">
              <path d="M10 20 Q90 70 170 20" stroke="#66a9d6" strokeWidth="4" fill="none" />
            </svg>
          </div>
          {/* Step 2 */}
          <div className="relative flex-1 flex flex-col items-center text-center z-10 md:mt-16">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-white bg-green-500 rounded-full w-12 h-12 flex items-center justify-center shadow-lg border-4 border-white mb-2">2</span>
              <span className="text-4xl md:text-5xl mb-2">📤</span>
            </div>
            <h4 className="font-semibold mt-2 mb-1 text-lg text-green-800">Upload Document</h4>
            <p className="text-gray-600 max-w-xs">Please follow the link sent via email to submit the necessary documents.</p>
          </div>
          {/* Connector 2->3 */}
          <div className="hidden md:block absolute left-[58%] top-28 w-[22%] h-24 z-0 pointer-events-none">
            <svg width="100%" height="100%" viewBox="0 0 180 80" fill="none">
              <path d="M10 60 Q90 10 170 60" stroke="#e63946" strokeWidth="4" fill="none" />
            </svg>
          </div>
          {/* Step 3 */}
          <div className="relative flex-1 flex flex-col items-center text-center z-10 md:mt-32">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-white bg-red-500 rounded-full w-12 h-12 flex items-center justify-center shadow-lg border-4 border-white mb-2">3</span>
              <span className="text-4xl md:text-5xl mb-2">✅</span>
            </div>
            <h4 className="font-semibold mt-2 mb-1 text-lg text-red-800">Get the sanction</h4>
            <p className="text-gray-600 max-w-xs">Receive the approval letter within 72 hours and receive funds in flat 24 hours post sanction</p>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Best Offers Section */}
      <section className="w-full py-12 bg-gradient-to-br from-blue-50 via-white to-blue-100">
        <h3 className="text-3xl font-bold text-blue-700 mb-10 text-center">Best offers on mortgage loans</h3>
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto px-4">
          {offers.map((offer, i) => (
            <div key={i} className="bg-gradient-to-br from-blue-100 via-white to-blue-200 rounded-2xl shadow-lg p-8 flex flex-col items-center text-center border border-blue-100">
              <div className="text-3xl font-extrabold text-blue-700 mb-2">{offer.rate}</div>
              <div className="text-base text-gray-700 mb-1">{offer.time}</div>
              <ul className="text-sm text-gray-600 mb-2">
                {offer.docs.map((doc, j) => (
                  <li key={j}>{doc}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider />

      {/* Testimonials Section */}
      <section className="w-full py-12 bg-gradient-to-r from-blue-50 via-white to-red-50">
        <h3 className="text-3xl font-bold text-blue-700 mb-10 text-center">Customer Stories</h3>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto px-4">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl shadow p-8 flex flex-col border border-blue-100 items-center text-center">
              <svg className="w-10 h-10 mb-4 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 17a4 4 0 01-4-4V7a4 4 0 014-4h2a4 4 0 014 4v6a4 4 0 01-4 4zm10 0a4 4 0 01-4-4V7a4 4 0 014-4h2a4 4 0 014 4v6a4 4 0 01-4 4z" /></svg>
              <div className="text-gray-700 text-lg mb-4 italic">"{t.quote}"</div>
              <div className="font-bold text-blue-800">{t.name}</div>
              <div className="text-sm text-gray-500">{t.title}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default MortgageLoanPage; 